/**
 * The company side panel's body (prototype board 3), opened over a company
 * list: which list it is opened from, the company, and four tabs -- Company,
 * People we know, Sources, Lists.
 *
 * Read-only: removing a company from a list and copying it to another were
 * taken out of the UI (2026-10-05); the API endpoints remain. The record
 * itself comes from `/companies/{id}`; while it loads, the list row the
 * recruiter clicked is already enough to draw the header.
 */
import type { CompanyListMember, CompanyListSummary, CompanyRecord } from "@nu-assist/contracts";
import { Spinner } from "@nu-assist/ui";
import { useState } from "react";

import type { CompanyListsClient } from "../../clients/companyLists.client";
import {
  CompanyHeader,
  CompanyListsOn,
  CompanyOverview,
  CompanySources,
  type CompanyTab,
  CompanyTabs,
  PeopleWeKnow,
  sourceCount,
} from "./CompanyRecordParts";
import { NoWebsiteOrLinkedinNote, hasNoWebsiteOrLinkedin } from "./NoWebsiteOrLinkedinNote";
import { useAvatarUrls, useCompanyPeople, useCompanyRecord, useLogoUrls } from "./queries";

interface CompanyViewProps {
  client: CompanyListsClient;
  list: CompanyListSummary;
  company: CompanyListMember;
  nameId: string;
}

/** A record-shaped stand-in from the list row, for the moment before the
 *  record arrives (no platforms, lists or people yet). */
function fromMember(m: CompanyListMember): CompanyRecord {
  return {
    ...m,
    created_at: m.added_at,
    updated_at: m.added_at,
    platforms: m.pitchbook_id
      ? [{ platform: "pitchbook", platform_id: m.pitchbook_id, linked: true }]
      : [],
    lists: [],
    people: { current: 0, alumni: 0 },
  };
}

export function CompanyView({ client, list, company, nameId }: CompanyViewProps) {
  const [tab, setTab] = useState<CompanyTab>("company");
  const recordQuery = useCompanyRecord(client, company.company_id);
  const peopleQuery = useCompanyPeople(client, company.company_id);
  const record = recordQuery.data ?? fromMember(company);
  const people = peopleQuery.data;
  const peopleCount = record.people.current + record.people.alumni;
  const logoUrls = useLogoUrls(client, [record]);
  const avatarUrls = useAvatarUrls(client, people?.items ?? []);

  return (
    <div className="flex flex-col" data-testid="company-view">
      <div
        className="border-b border-border bg-accent-muted px-6 py-2"
        data-testid="company-acting-on"
      >
        <p className="text-caption font-semibold uppercase tracking-wide text-accent-ink">
          Opened from
        </p>
        <p className="text-label text-text">
          Company list «{list.name}»
          {list.search_id_thrive && (
            <>
              {" "}
              → Search {list.search_id_thrive}
              {list.search_name ? ` · ${list.search_name}` : ""}
            </>
          )}
        </p>
      </div>

      <div className="flex flex-col gap-4 px-6 py-5">
        <CompanyHeader
          record={record}
          nameId={nameId}
          logoUrl={record.logo_s3_key ? logoUrls.get(record.logo_s3_key) : null}
        />

        {hasNoWebsiteOrLinkedin(company) && <NoWebsiteOrLinkedinNote />}

        <CompanyTabs
          active={tab}
          onSelect={setTab}
          counts={{
            people: peopleCount,
            sources: sourceCount(record, people),
            lists: recordQuery.data ? record.lists.length : 1,
          }}
        />

        {recordQuery.isPending && tab !== "company" ? (
          <Spinner label="Loading the company…" />
        ) : tab === "company" ? (
          <CompanyOverview
            record={record}
            people={people}
            avatarUrls={avatarUrls}
            onSeePeople={() => setTab("people")}
          />
        ) : tab === "people" ? (
          <PeopleWeKnow people={people} avatarUrls={avatarUrls} />
        ) : tab === "sources" ? (
          <CompanySources record={record} people={people} />
        ) : (
          <CompanyListsOn record={record} currentListId={list.list_id} />
        )}
      </div>
    </div>
  );
}
